import { Component, signal, WritableSignal, computed, Signal, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { ExternalDocumentService, UploadProgress } from '@app/services/entities/external-document.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { DocumentType } from '@app/types';
import { PageEvent } from '@angular/material/paginator';

interface FolioGroup {
  folio: string;
  files: File[];
  totalSize: number;
}

@Component({
  selector: 'app-external-document-upload',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule],
  templateUrl: './external-document-upload.component.html',
  styleUrls: ['./external-document-upload.component.scss']
})
export class ExternalDocumentUploadComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Inyección
  private externalDocumentService = inject(ExternalDocumentService);
  private snackBar = inject(CustomSnackBarService);
  private router = inject(Router);
  private customMessage = inject(CustomMessageService);

  // --- LEER ESTADO DEL SERVICIO ---
  uploadQueue: Signal<Map<string, File[]>> = this.externalDocumentService.uploadQueue;
  uploadProgress: Signal<UploadProgress | null> = this.externalDocumentService.uploadProgress;
  isUploading: Signal<boolean> = this.externalDocumentService.isUploading;

  selectedDocumentType: DocumentType | null = 'INVOICE';
  isDragging: WritableSignal<boolean> = signal(false);

  // Filtros UI
  folioFilterCtrl = new FormControl('');
  private folioFilterSignal: WritableSignal<string> = signal('');
  pageIndex: WritableSignal<number> = signal(0);
  pageSize: WritableSignal<number> = signal(10);

  // Computed signals
  allGroupsSignal: Signal<FolioGroup[]> = computed(() => {
    const groups: FolioGroup[] = [];
    this.uploadQueue().forEach((files, folio) => {
      groups.push({ folio, files, totalSize: files.reduce((s, f) => s + f.size, 0) });
    });
    return groups.sort((a, b) => a.folio.localeCompare(b.folio));
  });

  filteredGroupsSignal: Signal<FolioGroup[]> = computed(() => {
    const groups = this.allGroupsSignal();
    const filter = this.folioFilterSignal().toLowerCase();
    return filter ? groups.filter(g => g.folio.toLowerCase().includes(filter)) : groups;
  });

  paginatedGroupsSignal: Signal<FolioGroup[]> = computed(() => {
    const groups = this.filteredGroupsSignal();
    const start = this.pageIndex() * this.pageSize();
    return groups.slice(start, start + this.pageSize());
  });

  totalFilesSignal: Signal<number> = computed(() => {
    return this.allGroupsSignal().reduce((sum, g) => sum + g.files.length, 0);
  });

  constructor() {
    this.folioFilterCtrl.valueChanges.subscribe(val => {
      this.folioFilterSignal.set(val || '');
      this.pageIndex.set(0);
    });
  }

  // --- ACCIONES ---

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.externalDocumentService.addFilesToQueue(Array.from(input.files));
      input.value = '';
    }
  }

  onDrop(e: DragEvent) {
    e.preventDefault(); e.stopPropagation(); this.isDragging.set(false);
    if (e.dataTransfer?.files) {
      this.externalDocumentService.addFilesToQueue(Array.from(e.dataTransfer.files));
    }
  }

  removeFile(folio: string, index: number) {
    this.externalDocumentService.removeFileFromQueue(folio, index);
  }

  removeFolio(folio: string) {
    this.externalDocumentService.removeFolioFromQueue(folio, true);
  }

  clearAll() {
    this.externalDocumentService.clearQueue();
    this.folioFilterCtrl.setValue('');
  }

  uploadFiles(): void {
    const queueSize = this.uploadQueue().size;
    if (queueSize === 0) return;

    if (!this.selectedDocumentType) {
      this.snackBar.open('Seleccione tipo de documento.', 'warning');
      return;
    }

    // --- TRADUCCIÓN AMIGABLE ---
    let tipoAmigable: string = this.selectedDocumentType || '';
    if (this.selectedDocumentType === 'INVOICE') tipoAmigable = 'Facturas';
    else if (this.selectedDocumentType === 'REFERRAL') tipoAmigable = 'Remisiones';
    else if (this.selectedDocumentType === 'PRE_INVOICE') tipoAmigable = 'Pre-Facturas';

    this.customMessage.open({
      title: 'Confirmar Carga',
      message: `¿Subir ${queueSize} folios como <strong>${tipoAmigable}</strong>?`,
      type: 'warning',
      acceptBtn: true, dismissBtn: true
    }).then(confirmed => {
      if (confirmed) {
        this.externalDocumentService.initiateQueueUpload(this.selectedDocumentType!);
        this.snackBar.open('Carga iniciada. Los elementos desaparecerán al completarse.', 'info');
      }
    });
  }

  onDragOver(e: DragEvent) { e.preventDefault(); e.stopPropagation(); this.isDragging.set(true); }
  onDragLeave(e: DragEvent) { e.preventDefault(); e.stopPropagation(); this.isDragging.set(false); }
  handlePageEvent(e: PageEvent) { this.pageIndex.set(e.pageIndex); this.pageSize.set(e.pageSize); }

  getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'picture_as_pdf';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
    if (['doc', 'docx'].includes(ext)) return 'description';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'folder_zip';
    return 'insert_drive_file';
  }

  getFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toUpperCase() || 'FILE';
    return ext;
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }
}